import {
  ClusterOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  TrophyOutlined,
  UserOutlined,
  ApartmentOutlined,
  HistoryOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { Avatar, Popover, Space, Tag, Tooltip } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DeleteTopPlanApi from "../api/DeleteTopPlanApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../helpers/NotificationHelpers";
import { permissionAbility } from "../helpers/permissionHelpers";
import { getAmountInText, getNumberInText } from "../helpers/targetHelpers";
import { getProgressColor } from "../utils/progressColors";
import CascadePlanModal from "./modal/CascadePlanModal";
import EditTopPlanModal from "./modal/edit/EditTopPlanModal";
import HardDeleteModal from "./modal/HardDeleteModal";
import PlanHierarchyModal from "./modal/PlanHierarchyModal";
import TopPlansSpace from "./TopPlansSpace";
import { useEffect } from "react";
import UpdateTargetProgressModal from "./modal/UpdateTargetProgressModal";
import UpdateTargetApi from "../api/target-progress/UpdateTargetApi";

function TopPlanElement(props) {

  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editPlan, setEditPlan] = useState();
  const [cascadePlan, setCascadePlan] = useState();
  const [deletePlanId, setDeletePlanId] = useState("");
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const [activeKeys, setActiveKeys] = useState([]);
  const [cascade, setCascade] = useState(false);
  const [collapse, setCollapse] = useState(true);
  const [canCollapse, setCanCollapse] = useState(true);
  const [cascadedData, setCascadedData] = useState();
  const [cascadeCount, setCascadeCount] = useState(0);
  const [update, setUpdate] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [targetProgress, setTargetProgress] = useState(0);
  const [hierarchyModal, setHierarchyModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setTargetProgress(props.data?.achievedTarget || 0);
  }, [props.data]);

  useEffect(() => {
    if (props.data?.hasChildren) {
      setCanCollapse(true);
    } else {
      setCanCollapse(false);
    }
  }, [props.data]);

  function getTargetString(target, scale, abb) {
    let targetString = "";
    if (scale === "Number") {
      targetString = getNumberInText(target);
    } else if (scale === "Currency") {
      targetString = getAmountInText(target, abb);
    } else if (scale === "Percentage") {
      targetString = parseFloat(target).toFixed(2) + "%";
    } else {
      targetString = target + abb;
    }
    return targetString;
  }

  function getDisplayText() {
    // Check if this is a negative scale plan
    const isNegativeScale = Boolean(
      props.data?.isNegativeScale === true || 
      props.data?.isNegativeScale === 1 || 
      props.data?.isNegativeScale === "1" ||
      props.data?.isNegativeScale === "true"
    );

    if (isNegativeScale) {
      // For negative scale: show target with negative sign
      return {
        label: "Target:",
        value: getTargetString(
          -Math.abs(props.data?.target), // Show as negative value
          props.data?.scale?.scaleName,
          props.data?.scale?.scaleUnitAbbreviation
        ),
        isNegativeScale: true
      };
    } else {
      // For normal scale: show target
      return {
        label: "Target:",
        value: getTargetString(
          props.data?.target,
          props.data?.scale?.scaleName,
          props.data?.scale?.scaleUnitAbbreviation
        ),
        isNegativeScale: false
      };
    }
  }

  async function deletePlan() {
    const apiResponse = await DeleteTopPlanApi(deletePlanId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Top Plan deleted");

      props.handleDelete(deletePlanId);
      props.handleDeleteChange(props.data?.planId, props.totalChildCount - 1);
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setDeletePlanId("");
  }

  function handleExpand(element) {
    setActiveKeys([props.data?.planId]);
  }

  function handleCascade(cascadedPlan) {
    setCascadedData(cascadedPlan);
    setCascadeCount(cascadeCount + 1);
  }

  function handleDeleteChange(planId, totalChildCount) {
    totalChildCount === 0 && setCanCollapse(false);
  }

  const getBaseUrl = () => {
    if (location.pathname.includes("top-plans")) {
      return "/top-plans";
    } else if (location.pathname.includes("my-plans")) {
      return "/my-plans";
    } else {
      return "/";
    }
  };
  const handleNavigate = (planId) => {
    // Don't navigate if plan is inactive OR if any ancestor is inactive OR if any ancestor has cascadeFromSplitPlans enabled
    const isEffectivelyInactive = props.data?.isActive === 0 || props.data?.isActive === false || props.data?.hasInactiveAncestor === true || props.data?.hasAncestorWithCascadeFromSplitPlans === true;
    if (isEffectivelyInactive) {
      return; // Prevent navigation for inactive plans
    }
    const baseUrl = getBaseUrl();
    navigate(`${baseUrl}/${planId}`);
  };

  async function onFinish(values) {
    setSubmit(true);
    
    // FIXED: The UpdateTargetProgressModal already calculates the percentage
    // We should NOT recalculate here to avoid double calculation
    // The modal passes the already-calculated actual target value
    const actualTarget = values.target;
    
    const apiResponse = await UpdateTargetApi(
      props.data?.planId,
      actualTarget, // Send the already-calculated target from modal
      positionState?.id
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      if (apiResponse.targetProgress?.approvalStatus === "Approved") {
        setTargetProgress(actualTarget); // Update with actual target
        openNotificationSuccess("bottomRight", `Target updated`);
        
        // Trigger parent refresh to update colors immediately
        if (props.onTargetUpdate) {
          props.onTargetUpdate();
        }
      } else {
        openNotificationSuccess(
          "bottomRight",
          `Target updated but approval is pending`
        );
      }
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't update target. Try again later."
      );
    }
    setSubmit(false);
    setUpdate(false);
  }

  function getPerformance() {
    let output = 0;

    // For cascade-from-split-plans, the backend calculates performance directly
    // achievedTarget is 0 (no direct progress records), so use the pre-calculated value
    if (props.data?.cascadeFromSplitPlans) {
      output = parseFloat(props.data?.performance || 0);
      return isNaN(output) ? 0 : output;
    }

    // Check if this is a negative scale plan
    const isNegativeScale = Boolean(
      props.data?.isNegativeScale === true || 
      props.data?.isNegativeScale === 1 || 
      props.data?.isNegativeScale === "1" ||
      props.data?.isNegativeScale === "true"
    );

    if (isNegativeScale) {
      // NEGATIVE SCALE LOGIC: Show remaining percentage (inverted)
      // Target represents the starting negative value (e.g., 20M losses)
      // targetProgress represents positive reductions (e.g., 5M loss reduction)
      const startingValue = Math.abs(props.data?.target || 0);
      const achievedReduction = targetProgress || 0;
      
      if (startingValue > 0) {
        // Calculate remaining percentage (100% - achieved percentage)
        const achievedPercentage = (achievedReduction / startingValue) * 100;
        output = (100 - achievedPercentage).toFixed(2);
        // Ensure it doesn't go below 0%
        output = Math.max(output, 0);
      }


    } else {
      // NORMAL SCALE LOGIC: Standard percentage calculation
      output = ((targetProgress / props.data?.target) * 100).toFixed(2);
    }

    if (isNaN(output)) {
      output = 0;
    }
    return output;
  }

  return (
    <>
      <>
        <div
          className={"flex items-center rounded-md " + ((props.data?.isActive === 0 || props.data?.isActive === false || props.data?.hasInactiveAncestor === true || props.data?.hasAncestorWithCascadeFromSplitPlans === true) ? "cursor-not-allowed" : "cursor-pointer")}
          onClick={() => handleNavigate(props.data?.planId)}
        >
          <div className="flex justify-center items-center w-8">
            {canCollapse ? (
              collapse ? (
                <PlusSquareOutlined
                  className="cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleExpand(props.data?.planId);
                    setCollapse(!collapse);
                  }}
                />
              ) : (
                <MinusSquareOutlined
                  className="cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCollapse(!collapse);
                  }}
                />
              )
            ) : (
              ""
            )}
          </div>
          <div className={"grid grid-cols-5 ml-2 p-3 border gap-5 w-full" + ((props.data?.isActive === 0 || props.data?.isActive === false || props.data?.hasInactiveAncestor === true || props.data?.hasAncestorWithCascadeFromSplitPlans === true) ? " opacity-50 bg-gray-100" : "")}>
            <div className="col-span-3">
              <div className="mb-1">
                {props.data?.cascadeFrom?.title && (
                  <>
                    <Tooltip title={props.data?.cascadeFrom?.title}>
                      <ClusterOutlined />
                    </Tooltip>{" "}
                    -{" "}
                  </>
                )}
                {props.data?.title}
                {(props.data?.finalized === 1 || props.data?.finalized === "1" || props.data?.finalized === true) && (
                  <CheckCircleFilled
                    style={{
                      color: '#90EE90',
                      marginLeft: '8px',
                      fontSize: '16px'
                    }}
                    title="Plan Finalized"
                  />
                )}{" "}
                <Tag>{props.data?.planGroup.toUpperCase()}</Tag>
              </div>
              <div className="flex items-center gap-1">
                <div>
                  <Tag
                    color={getProgressColor(getPerformance() || 0)}
                  >
                    {(getPerformance() || "0") + "%"}
                  </Tag>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {getDisplayText().label}
                  </span>{" "}
                  <span style={{ 
                    fontWeight: getDisplayText().isNegativeScale ? 'bold' : 'normal',
                    color: getDisplayText().isNegativeScale ? '#ff4d4f' : 'inherit'
                  }}>
                    {getDisplayText().value}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-span-2 flex justify-between items-start">
              <div>
                <div className="flex items-center">
                  {props.data?.assignedTo?.imageUrl ? (
                    <Avatar
                      style={{ minWidth: "32px" }}
                      className="cursor-pointer"
                      src={props.data?.assignedTo?.imageUrl}
                    />
                  ) : (
                    <Avatar style={{ minWidth: "32px" }}>
                      {props.data?.assignedTo?.firstName ? (
                        props.data?.assignedTo?.firstName
                          ?.charAt(0)
                          .toUpperCase()
                      ) : (
                        <UserOutlined />
                      )}
                    </Avatar>
                  )}
                  <div className="ml-2">
                    <div>{props.data?.assignedTo?.firstName || "-"}</div>
                    <div className="flex items-center gap-1 -mt-1">
                      <div className="text-xs ">
                        {props.data?.assignedTo?.designationAbb},
                      </div>
                      <div className="flex items-center">
                        <div
                          className="min-w-[8px] h-2 mr-1 rounded-full"
                          style={{
                            backgroundColor: props.data?.unit?.orgLevelColor,
                          }}
                        />
                        <div className="truncate">{props.data?.unit?.name}</div>
                        <Popover
                          placement="top"
                          content={
                            <div className="text-xs">
                              {props.data?.unit?.name}
                              {props.data?.unit?.headUnit &&
                                " < " + props.data?.unit?.headUnit?.name}
                              {props.data?.unit?.headUnit?.headUnit &&
                                " < " +
                                props.data?.unit?.headUnit?.headUnit?.name}
                            </div>
                          }
                          trigger="hover"
                        >
                          <div className="text-gray-500 text-xs ml-1">
                            <InfoCircleOutlined />
                          </div>
                        </Popover>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Space>
                {props.data?.canUpdate &&
                  positionState?.id === props.data?.assignedTo?.positionId &&
                  !props.data?.cascadeFromSplitPlans ? (
                    <Tooltip title={"Update Target"}>
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          setUpdate(true);
                        }}
                        className="text-blue-500 hover:text-blue-600 cursor-pointer"
                      >
                        <TrophyOutlined />
                      </div>
                    </Tooltip>
                  ) : positionState?.id === props.data?.assignedTo?.positionId &&
                    props.data?.cascadeFromSplitPlans ? (
                    <Tooltip title={"Update disabled - This plan uses split plans for progress"}>
                      <div className="text-gray-400 cursor-not-allowed" style={{ opacity: 0.5 }}>
                        <TrophyOutlined />
                      </div>
                    </Tooltip>
                  ) : positionState?.id === props.data?.assignedTo?.positionId &&
                    props.data?.hasSubTargets ? (
                    <Tooltip title={"Update disabled - This plan uses sub-targets for progress"}>
                      <div className="text-gray-400 cursor-not-allowed" style={{ opacity: 0.5 }}>
                        <TrophyOutlined />
                      </div>
                    </Tooltip>
                  ) : null}
                {permissionAbility(userLevel, "plans", "edit") && (
                  <Tooltip title={props.data?.cascadeFromSplitPlans ? "Cascade disabled - This plan uses split plans for progress" : "Cascade"}>
                    <div
                      onClick={(event) => {
                        if (!props.data?.cascadeFromSplitPlans) {
                          event.stopPropagation();
                          setCascade(true);
                          setCascadePlan(props.data);
                        }
                      }}
                      className={props.data?.cascadeFromSplitPlans ? "text-gray-400 cursor-not-allowed" : "text-blue-500 hover:text-blue-600 cursor-pointer"}
                      style={{ opacity: props.data?.cascadeFromSplitPlans ? 0.5 : 1 }}
                    >
                      <ClusterOutlined />
                    </div>
                  </Tooltip>
                )}
                {permissionAbility(userLevel, "plans", "edit") && (
                  <Tooltip title={"Edit"}>
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditPlan(props.data);
                        setEditModal(true);
                      }}
                      className="text-blue-500 hover:text-blue-600 cursor-pointer"
                    >
                      <EditOutlined />
                    </div>
                  </Tooltip>
                )}
                {permissionAbility(userLevel, "plans", "edit") && (
                  <Tooltip title={"Manage Hierarchy"}>
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        setHierarchyModal(true);
                      }}
                      className="text-green-500 hover:text-green-600 cursor-pointer"
                    >
                      <ApartmentOutlined />
                    </div>
                  </Tooltip>
                )}
                {permissionAbility(userLevel, "plans", "delete") &&
                  (!canCollapse ? (
                    <Tooltip title={"Delete"}>
                      <div
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setDeletePlanId(props.data?.planId);
                          setDeleteModal(true);
                        }}
                        className="text-red-500 hover:text-red-600 cursor-pointer"
                      >
                        <DeleteOutlined />
                      </div>
                    </Tooltip>
                  ) : (
                    <Popover
                      placement="left"
                      content={
                        <div className="text-xs w-48">
                          Can't delete this element. This plan was cascaded.
                        </div>
                      }
                      trigger="hover"
                    >
                      <div className="text-gray-500">
                        <DeleteOutlined />
                      </div>
                    </Popover>
                  ))}
              </Space>
            </div>
          </div>
        </div>
        <div className={"ml-5 " + (collapse ? "hidden" : "block")}>
          <TopPlansSpace
            plans={props.plans}
            planId={props.data?.planId}
            activeKeys={activeKeys}
            cascadedData={cascadedData}
            collapse={collapse}
            handleDeleteChange={(planId, totalChildCount) =>
              handleDeleteChange(planId, totalChildCount)
            }
            cascadeCount={cascadeCount}
          />
        </div>
      </>

      <EditTopPlanModal
        visible={editModal}
        handleCancel={() => {
          setEditPlan();
          setEditModal(false);
        }}
        editPlan={editPlan}
        handleOk={(data) => props.handleEdit(data)}
      />
      <HardDeleteModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal(false)}
        handleOk={() => {
          deletePlan();
        }}
        text={`Are you sure want to delete this ${topPlanState}?`}
        title={`Delete ${topPlanState}`}
      />
      <CascadePlanModal
        visible={cascade}
        handleCancel={() => {
          setCascadePlan();
          setCascade(false);
        }}
        plan={cascadePlan}
        handleOk={(cascadedPlan) => {
          setCanCollapse(true);
          handleCascade(cascadedPlan);
          setCollapse(false);
        }}
      />

      {update && (
        <UpdateTargetProgressModal
          visible={update}
          handleCancel={() => setUpdate(false)}
          data={props.data}
          targetProgress={targetProgress}
          onFinish={onFinish}
          submit={submit}
        />
      )}

      <PlanHierarchyModal
        visible={hierarchyModal}
        onCancel={() => setHierarchyModal(false)}
        planId={props.data?.planId}
        planTitle={props.data?.title}
        onHierarchyChange={() => {
          // Refresh the child plans if needed
          if (props.onHierarchyChange) {
            props.onHierarchyChange();
          }
        }}
      />
    </>
  );
}

export default TopPlanElement;
